import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { PghUserDeviceBase } from '../../../../shared/device/pgh-user-device';
import { EquipmentInfoDto } from '../../../../shared/serial-search/serial-search-response.dto';
import { buildDeviceDisplayName } from '../../../../shared/device/device-display-name.pipe';
import { SelectionPoint } from '../../../../shared/array-object-selector/selection-point';
import { ADPResourceService, CustomFormControl } from 'digital-core';
import { isEmpty as _isEmpty } from 'lodash';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { DeviceDto } from '../../../../core/services/device-inventory/device.dto';
import { DeviceInfoFormResult } from '../cellular-device-info-form/cellular-device-info-form.component';
import { Subject, takeUntil } from 'rxjs';
import { EquipmentSearchService } from '../../../../shared/equipment-search/equipment-search.service';
import { EssItemGroup } from '../../../../shared/equipment-search/ess-item-group';
import { HttpErrorResponse } from '@angular/common/http';
import { format } from 'date-fns';

@Component({
  selector: 'pgh-non-cellular-device-info-form',
  templateUrl: './non-cellular-device-info-form.component.html',
  styleUrls: ['./non-cellular-device-info-form.component.scss']
})
export class NonCellularDeviceInfoFormComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>();

  @Input() public preHeaderTextSfKey?: string;
  @Input() public headerTextSfKey?: string;
  @Input() public bodyTextSfKey?: string;
  @Input() public submitButtonLabelSfKey?: string;

  
  /**
   * This is the device the user had already selected in a previous step in
   * whatever flow they are in. There are some flows where the user won't be
   * selecting a device before seeing this component so null/undefined are
   * valid and supported values.
   */
  @Input() public preSelectedDevice?: PghUserDeviceBase;

  @Input() public devicesFromAllInventories?: DeviceDto[];

  /**
   * The type of device that we need to be gathering the info for.
   * Like is it a tablet? Smart watch?
   * This is a required input.
   */
  @Input() public deviceType?: EssItemGroup;

  @Output() public formSubmitted = new EventEmitter<DeviceInfoFormResult>();

  public performingDataFetch = false;

  public preHeaderTextContext?: { device: string };

  public deviceInfoForm = new FormGroup({
    make: new FormControl<string>(undefined),
    model: new FormControl<string>(undefined),
    selectedDevice: new FormControl<EquipmentInfoDto>(undefined, [Validators.required]),
    nickname: new CustomFormControl('', (control) => this.validateNicknameIsUnique(control)),
  });

  public selectionPoints?: SelectionPoint[];  
  
  public makes?: string[];
  public models?: string[];
  public devicesToChooseFrom?: EquipmentInfoDto[];

  public constructor(
    public resourceService: ADPResourceService,
    private equipmentSearchService: EquipmentSearchService,
  ) { }

  public ngOnInit(): void {
    this.preHeaderTextContext = { device: buildDeviceDisplayName(this.preSelectedDevice) };

    this.deviceInfoForm.controls.make.valueChanges.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe( { next: (value) => this.makeSelectionChanged(value) });
    this.deviceInfoForm.controls.model.valueChanges.pipe(
      takeUntil(this.onDestroy$),
    ).subscribe( { next: (value) => this.modelSelectionChanged(value) });

    this.selectionPoints = this.buildSelectionPoints();

    this.performingDataFetch = true;
    this.equipmentSearchService.getManufacturersOfWiFiOnlyDevicesFromOracleIndex(this.deviceType).subscribe({
      next: (res) => {
        if ((res?.length ?? 0) < 1) {
          this.throwEssIndexIsEmptyError();
        }

        this.makes = res;

        // Pre-select the make if they already searched a device.
        if (this.preSelectedDevice != null) {
          const foundMake = this.makes.find((m) => m?.toLowerCase() === this.preSelectedDevice?.make?.toLowerCase());

          if (foundMake != null) {
            this.deviceInfoForm.controls.make.setValue(foundMake);
          }
        }

        this.performingDataFetch = false;
      },
      error: (err) => {
        throw err;
      }
    });
  }

  public ngOnDestroy(): void {
    this.formSubmitted.complete();
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
  
  public makeSelectionChanged(newMake: string) {
    this.models = [];
    this.deviceInfoForm.controls.model.setValue(undefined);

    this.devicesToChooseFrom = [];
    this.deviceInfoForm.controls.selectedDevice.setValue(undefined);

    this.deviceInfoForm.controls.nickname.setValue(undefined);

    this.performingDataFetch = true;
    this.equipmentSearchService.getModelsOfWiFiOnlyDevicesForManufacturerFromOracleIndex(newMake, this.deviceType).subscribe({
      next: (res) => {
        this.performingDataFetch = false;

        if ((res?.length ?? 0) < 1) {
          this.throwEssIndexIsEmptyError();
        }

        this.models = res;
      },
      error: (err) => {
        this.performingDataFetch = false;
        throw err;
      },
    });
  }

  public modelSelectionChanged(newModel: string) {
    if (newModel == null) {
      return;
    }

    this.devicesToChooseFrom = [];
    this.deviceInfoForm.controls.selectedDevice.setValue(undefined);

    this.deviceInfoForm.controls.nickname.setValue(undefined);

    this.performingDataFetch = true;
    this.equipmentSearchService.findWiFiOnlyDevicesByManufacturerAndModelAndDeviceTypeFromOracleIndex(this.deviceInfoForm.controls.make.value, newModel, this.deviceType).subscribe({
      next: (res) => {
        this.performingDataFetch = false;

        if ((res?.Value?.length ?? 0) < 1) {
          this.throwEssIndexIsEmptyError();
        }

        this.devicesToChooseFrom = res?.Value;
      },
      error: (err) => {
        this.performingDataFetch = false;
        throw err;
      },
    });
  }

  public handleDeviceSelectionChanged(newlySelectedDevice: EquipmentInfoDto) {
    this.deviceInfoForm.controls.selectedDevice.setValue(newlySelectedDevice);
    this.setNicknameField();
  }

  public handleFormSubmitted() {
    if (this.deviceInfoForm.invalid) {
      return;
    }

    this.formSubmitted.emit({
      equipmentInfo: this.deviceInfoForm.controls.selectedDevice.value,
      deviceNickname: this.deviceInfoForm.controls.nickname.value,
    });
  }

  public handleMultipleObjectsDetected(objectsDetected: EquipmentInfoDto[]) {
    // The idea Anthony had on 2024-08-09 is that we have asked enough questions and narrowed down the big costly options
    // (like memory size, carrier, etc...), so in the event that we didn't drill down to one device somehow, then let's just
    // grab one of them because it doesn't matter from a claim's cost perspective.

    console.warn("There were multiple device options and the user has selected all the values we asked them. This is not expected; we expected to drill down to only one device option. Here are the device options that were left after the user made their selection:", objectsDetected);

    this.handleDeviceSelectionChanged(objectsDetected[0]);
  }

  private setNicknameField() {
    if (!_isEmpty(this.preSelectedDevice?.nickName?.trim())) {
      this.deviceInfoForm.controls.nickname.setValue(this.preSelectedDevice?.nickName?.trim());
      this.deviceInfoForm.controls.nickname.markAsTouched();
      return;
    }

    const selectedDevice = this.deviceInfoForm.controls.selectedDevice.value;
    if (selectedDevice == null) {
      return;
    }

    let nickname = `${selectedDevice.Manufacturer ?? ''} ${selectedDevice.MarketingName ?? ''}`.trim();
    let counter = 1;

    while (!this.isNicknameUnique(nickname)) {
      counter++;
      nickname = `${selectedDevice.Manufacturer ?? ''} ${selectedDevice.MarketingName ?? ''} ${counter}`.trim();
    }

    this.deviceInfoForm.controls.nickname.setValue(nickname);
    this.deviceInfoForm.controls.nickname.markAsTouched();
  }

  private buildSelectionPoints(): SelectionPoint[] {
    return [
      {
        propertyPath: 'Attributes.StorageSize',
        fieldLabel: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_StorageCapacityField_Label'),
        displayValueForNullUndefinedEmptyOrWhitespace: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_StorageCapacityField_ValueToDisplayWhenNullOption'),
      },
      {
        propertyPath: 'Attributes.Color',
        fieldLabel: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_ColorField_Label'),
        displayValueForNullUndefinedEmptyOrWhitespace: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_ColorField_ValueToDisplayWhenNullOption'),
      },
      {
        propertyPath: 'Attributes.Material',
        fieldLabel: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_MaterialField_Label'),
        displayValueForNullUndefinedEmptyOrWhitespace: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_MaterialField_ValueToDisplayWhenNullOption'),
      },
      {
        propertyPath: 'Attributes.ModelNumber',
        fieldLabel: this.resourceService.getViewLabel('RegisterNonCellularDevice_DeviceInfoForm_ModelNumberField_Label'),
        displayValueForNullUndefinedEmptyOrWhitespace: this.resourceService.getViewLabel('RegisterNonCellularDevice_DeviceInfoForm_ModelNumberField_ValueToDisplayWhenNullOption'),
      },
      {
        propertyPath: 'Attributes.Carrier',
        fieldLabel: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_CarrierField_Label'),
        displayValueForNullUndefinedEmptyOrWhitespace: this.resourceService.getViewLabel('RegisterCellularDevice_DeviceInfoForm_CarrierField_ValueToDisplayWhenNullOption'),
      },
    ];
  }

  private isNicknameUnique(nickname: string) {
    return !(this.devicesFromAllInventories?.map((device) => device.nickName?.toLowerCase()) ?? []).includes(nickname.toLowerCase());
  }

  private validateNicknameIsUnique(control: AbstractControl<any, any>): ValidationErrors {
    if (control?.value != null) {
      return this.isNicknameUnique(control.value?.trim()) ? null : { uniqueNickname: true };
    }

    return null;
  }

  private throwEssIndexIsEmptyError() {
    throw new HttpErrorResponse({
      error: {
        ErrorId: `PGH-UI-${format(new Date(), 'yyyyMMdd-HHmmssSSS')}`,
        Source: 'EquipmentSearchService_OracleUsEquipment',
        ErrorCode: 'Ess_Index_Empty'
      }
    });
  }
}
